// ═══════════════════════════════════════════════════════════════
//  AppRouter.jsx  — All routes covered, all admin pages protected
// ═══════════════════════════════════════════════════════════════

import React from "react";
import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "../../../AuthProvider/AuthContext";
import { ENABLE_TRAINS } from "../../../config/features";
// ── Pages ─────────────────────────────────────────────────────
import Home from "../../Home/Home";
import FlightResults from "../../Features/Flights/Flights";
import Navbar from "../Navbar/Nav";
import Footer from "../Footer/Footer";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import PageScroll from "../ScrollToTop/PageScroll";
import BusesPage from "../../Features/Buses/Bus";
import BusBooking from "../../Features/Buses/Busbooking/Busbooking.jsx";
import BusConfirm from "../../Features/Buses/Busbooking/Busconfirm.jsx";
import TrainsPage from "../../Features/Trains/TrainPage.jsx";
import Trainbooking from "../../Features/Trains/TrainBooking/Trainbooking.jsx";
import HotelPage from "../../Features/Hotels/HotelPage";
import PrivacyPolicy from "../../Legal/Privacy/Privacy.jsx";
import TermsAndConditions from "../../Legal/Terms/terms.jsx";
import PaymentSecurity from "../../Legal/Payment Security/payment-security.jsx";
import CancellationRefund from "../../Legal/Cancellation Refund/cancellationRefund.jsx";
import Disclaimer from "../../Legal/Disclaimer/disclaimer.jsx";
import FlightTerms from "../../Legal/Flight Terms/flightTerms.jsx";
import BusTerms from "../../Legal/Bus Terms/busTerms.jsx";
import HolidayTerms from "../../Legal/Holiday Terms/holidayTerms.jsx";
import HotelTerms from "../../Legal/Hotel Terms/hotelTerms.jsx";
import VisaTerms from "../../Legal/Visa Terms/visaTerms.jsx";

import HotelBooking from "../../Features/Hotels/Hotelbooking";
import HotelConfirm from "../../Features/Hotels/HotelConfirm/HotelConfirm.jsx";
import ContactUs from "../../Contact/ContactPage";
import Holidays from "../../Features/Holidays/Holiday";
import Packages from "../../Features/Holidays/Packages/Packages.jsx";
import HolidayBooking from "../../Features/Holidays/Booking holiday/Booking.jsx";
import HolidayConfirm from "../../Features/Holidays/Confirm booking/Confirm.jsx";
import Main from "../../Features/Offers/Offer";
import VisaPage from "../../Features/Visa/Visa";
import VisaTypePage from "../../Features/Visa/Visa type/Type.jsx";
import VisaApplicationForm from "../../Features/Visa/Form/form.jsx";
import About from "../../About/About";
import Activities from "../../Features/Activities/Activities.jsx";
import CitiesActivities from "../../Features/Activities/Cities_activities/Cities_activities.jsx";
import InternationalActivities from "../../Features/Activities/Cities_activities/International.jsx";
import ActivityConfirm from "../../Features/Activities/Confirm activity/Confirm.jsx";
import ActivityPayment from "../../Features/Activities/Activity payment/Payment.jsx";
import AdminDashboard from "../../Admin Dashboard/Admin";
import ActivityAdmin from "../../Admin Dashboard/ActivityAdmin/ActivityAdmin.jsx";
import { ComparePage } from "../../Features/Flights/Compare/Compare";
import UserProfile from "../../User Profile/UserProfile";

// ── Admin Auth ────────────────────────────────────────────────
import { AdminAuthProvider } from "../../Admin Dashboard/AdminAuthContext.jsx";
import AdminLogin from "../../Admin Dashboard/AdminLogin";
import ProtectedAdminRoute from "../../Admin Dashboard/ProtectedAdminRoute";
import CancelBooking from "../../CancelBooking/CancelBooking.jsx";

// ══════════════════════════════════════════════════════════════
//  404
// ══════════════════════════════════════════════════════════════
function NotFound() {
  return (
    <div style={{ padding: "80px 24px", textAlign: "center", color: "#1f4f45" }}>
      <h1 style={{ fontSize: "2rem", marginBottom: "12px" }}>Page not found</h1>
      <p style={{ marginBottom: "20px", color: "#2f6c57" }}>
        The page you are looking for does not exist.
      </p>
      <Link to="/" style={{ color: "#0f766e", textDecoration: "underline" }}>
        Go back home
      </Link>
    </div>
  );
}

function TrainsGate({ children }) {
  if (ENABLE_TRAINS) {
    return children;
  }

  return <Navigate to="/" replace />;
}

// ══════════════════════════════════════════════════════════════
//  Shells
// ══════════════════════════════════════════════════════════════

/** Admin shell — NO Navbar / Footer / Loader */
function AdminShell({ children }) {
  return <>{children}</>;
}


/** Public shell — Navbar + Loader + Footer + scroll helpers */
function PublicShell({ children }) {
  return (
    <>
      <Navbar />
      <PageScroll />
      {children}
      <Footer />
      <ScrollToTop />
    </>
  );
}

function GoogleAuthCallback() {
  const { login, fetchCurrentUser } = useAuth();

  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  React.useEffect(() => {
    let cancelled = false;

    async function completeGoogleLogin() {
      if (!token) {
        window.location.replace("/");
        return;
      }

      try {
        const user = await fetchCurrentUser(token);

        if (cancelled) return;

        login(user, token);
        window.location.replace("/");
      } catch (error) {
        console.error("[GOOGLE AUTH] Callback failed:", error);

        localStorage.removeItem("mbt_token");
        localStorage.removeItem("mbt_user");

        if (!cancelled) {
          window.location.replace("/");
        }
      }
    }

    completeGoogleLogin();

    return () => {
      cancelled = true;
    };
  }, [token, fetchCurrentUser, login]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1f4f45",
      }}
    >
      Signing you in with Google...
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  Router
// ══════════════════════════════════════════════════════════════
export default function AppRouter() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminAuthProvider>
          <div style={{ minHeight: "100vh", background: "#f7fcfb" }}>
          <Routes>

            {/* ────────────────────────────────────────────────
                PUBLIC ROUTES  (Navbar + Footer)
            ──────────────────────────────────────────────── */}
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />

            {/* Flights */}
            <Route path="/flights" element={<PublicShell><FlightResults /></PublicShell>} />
            <Route path="/compare" element={<PublicShell><ComparePage /></PublicShell>} />

            {/* Hotels */}
            <Route path="/hotels" element={<PublicShell><HotelPage /></PublicShell>} />
            <Route path="/hotelbooking/:hotelId" element={<PublicShell><HotelBooking /></PublicShell>} />
            <Route path="/hotel-confirm" element={<PublicShell><HotelConfirm /></PublicShell>} />

            {/* Trains */}
<Route
  path="/trains"
  element={
    <TrainsGate>
      <PublicShell>
        <TrainsPage />
      </PublicShell>
    </TrainsGate>
  }
/>

<Route
  path="/trainbooking"
  element={
    <TrainsGate>
      <PublicShell>
        <Trainbooking />
      </PublicShell>
    </TrainsGate>
  }
/>

<Route
  path="/trainbooking/:trainId"
  element={
    <TrainsGate>
      <PublicShell>
        <Trainbooking />
      </PublicShell>
    </TrainsGate>
  }
/>



            {/* Buses */}
            <Route path="/buses" element={<PublicShell><BusesPage /></PublicShell>} />
            <Route path="/busbooking" element={<PublicShell><BusBooking /></PublicShell>} />
            <Route path="/busconfirm" element={<PublicShell><BusConfirm /></PublicShell>} />

            {/* Holidays */}
            <Route path="/holidays" element={<PublicShell><Holidays /></PublicShell>} />
            <Route path="/holidays/packages/:place" element={<PublicShell><Packages /></PublicShell>} />
            <Route path="/holidays/booking/:place/:packageSlug" element={<PublicShell><HolidayBooking /></PublicShell>} />
            <Route path="/holidays/confirm/:place/:packageSlug" element={<PublicShell><HolidayConfirm /></PublicShell>} />
            <Route path="/cancel-booking/:type/:id" element={<CancelBooking />} />
            {/* Activities */}
            <Route path="/activities" element={<PublicShell><Activities /></PublicShell>} />
            <Route path="/activities/confirm" element={<PublicShell><ActivityConfirm /></PublicShell>} />
            <Route path="/activities/payment" element={<PublicShell><ActivityPayment /></PublicShell>} />
            {/* canonical path */}
            <Route path="/cities-activities" element={<PublicShell><CitiesActivities /></PublicShell>} />
            {/* legacy capitalised alias → redirect to canonical */}
            <Route path="/Cities_activities" element={<Navigate to="/cities-activities" replace />} />
            <Route path="/international-activities" element={<PublicShell><InternationalActivities /></PublicShell>} />

            {/* Visa */}
            <Route path="/visa" element={<PublicShell><VisaPage /></PublicShell>} />
            <Route path="/visa/type/:countryCode" element={<PublicShell><VisaTypePage /></PublicShell>} />
            <Route path="/visa/form" element={<PublicShell><VisaApplicationForm /></PublicShell>} />

            {/* Misc public */}
            <Route path="/about" element={<PublicShell><About /></PublicShell>} />
            <Route path="/contact" element={<PublicShell><ContactUs /></PublicShell>} />
            <Route path="/offer" element={<PublicShell><Main /></PublicShell>} />
            <Route path="/profile" element={<PublicShell><UserProfile /></PublicShell>} />
           {/* Legal */}
<Route path="/privacy" element={<PublicShell><PrivacyPolicy /></PublicShell>} />
<Route path="/terms" element={<PublicShell><TermsAndConditions /></PublicShell>} />
<Route path="/payment-security" element={<PublicShell><PaymentSecurity /></PublicShell>} />
<Route path="/cancellation-refund" element={<PublicShell><CancellationRefund /></PublicShell>} />
<Route path="/disclaimer" element={<PublicShell><Disclaimer /></PublicShell>} />
<Route path="/flight-terms" element={<PublicShell><FlightTerms /></PublicShell>} />
<Route path="/bus-terms" element={<PublicShell><BusTerms /></PublicShell>} />
<Route path="/holiday-terms" element={<PublicShell><HolidayTerms /></PublicShell>} />
<Route path="/hotel-terms" element={<PublicShell><HotelTerms /></PublicShell>} />
<Route path="/visa-terms" element={<PublicShell><VisaTerms /></PublicShell>} />

            {/* ────────────────────────────────────────────────
                ADMIN LOGIN  (public, no shell chrome)
            ──────────────────────────────────────────────── */}
            <Route
              path="/admin/login"
              element={<AdminShell><AdminLogin /></AdminShell>}
            />

            {/* ────────────────────────────────────────────────
                PROTECTED ADMIN ROUTES
                All wrapped in ProtectedAdminRoute so any
                unauthenticated visit redirects to /admin/login
            ──────────────────────────────────────────────── */}

            {/* Main dashboard */}
            <Route
              path="/admin"
              element={
                <ProtectedAdminRoute>
                  <AdminShell><AdminDashboard /></AdminShell>
                </ProtectedAdminRoute>
              }
            />

            {/* Activity admin — canonical path */}
            <Route
              path="/admin/activity-admin"
              element={
                <ProtectedAdminRoute>
                  <AdminShell><ActivityAdmin /></AdminShell>
                </ProtectedAdminRoute>
              }
            />

            {/* Activity admin — alternate capitalisation (legacy) */}
            <Route
              path="/admin/ActivityAdmin"
              element={
                <ProtectedAdminRoute>
                  <AdminShell><ActivityAdmin /></AdminShell>
                </ProtectedAdminRoute>
              }
            />

            {/* Legacy top-level paths → redirect to protected canonical paths */}
            <Route
              path="/activity-admin"
              element={
                <ProtectedAdminRoute>
                  <Navigate to="/admin/activity-admin" replace />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/ActivityAdmin"
              element={
                <ProtectedAdminRoute>
                  <Navigate to="/admin/activity-admin" replace />
                </ProtectedAdminRoute>
              }
            />
            
            <Route
  path="/auth/google/callback"
  element={<GoogleAuthCallback />}
/>

            {/* ────────────────────────────────────────────────
                404  — must be last
            ──────────────────────────────────────────────── */}
            <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />

          </Routes>
        </div>
      </AdminAuthProvider>
    </AuthProvider>
  </BrowserRouter>
  );
}
